/* eslint-disable max-lines */
import { pageSchema } from "@/helpers/types";
import { z } from "zod";
import { BaseTagSchema } from "../settings/tags/types";

/**
 * Enum for dataset detail page tab values
 */
export enum DatasetTab {
  SCHEMA = "schema",
  LINEAGE = "lineage",
  OPPORTUNITIES = "opportunities",
  ACCESS_HISTORY = "access-history",
  QUERIES = "queries",
}

export const TableDataFiltersSchema = z.object({
  page: z.number().optional(),
  size: z.number().optional(),
  table_rk: z.array(z.string()).optional(),
  filter_schema: z.array(z.string()).optional(),
  filter_db: z.array(z.string()).optional(),
  order_by: z.string().optional(),
  sort_order: z.string().optional(),
  total_cost: z.string().optional(),
  insertion_cost: z.string().optional(),
  storage_cost: z.string().optional(),
  clustering_cost: z.string().optional(),
  tag_rks: z.array(z.string()).optional(),
  min_reads: z.string().optional(),
  min_writes: z.string().optional(),
  min_read_write_ratio: z.string().optional(),
  owners: z.array(z.string()).optional(),
  instance_ids: z.array(z.string()).optional(),
  snowflakeTags: z.array(z.string()).optional(),
});

export type TableDataFilters = z.infer<typeof TableDataFiltersSchema>;

/**
 * Snowflake object types accepted by the `table_types` datasets filter.
 * `value` is the API slug, `label` is what the user sees.
 */
export const TABLE_TYPE_FILTER_OPTIONS = [
  { value: "dynamic_table", label: "Dynamic Table" },
  { value: "materialized_view", label: "Materialized View" },
  { value: "secure_view", label: "Secure View" },
  { value: "view", label: "View" },
  { value: "external_table", label: "External Table" },
  { value: "event_table", label: "Event Table" },
  { value: "transient_table", label: "Transient Table" },
  { value: "temporary_table", label: "Temporary Table" },
  { value: "permanent_table", label: "Permanent Table" },
  { value: "iceberg_table", label: "Iceberg Table" },
  { value: "hybrid_table", label: "Hybrid Table" },
  { value: "immutable_table", label: "Immutable Table" },
];

export const DatasetSchema = z.object({
  rk: z.object({
    rk: z.string(),
    name: z.string(),
    path: z.string(),
  }),
  resource_id: z.number(),
  total_cost: z.number(),
  insertion_cost: z.number(),
  storage_cost: z.number(),
  clustering_cost: z.number(),
  tag_rks: z.array(z.string()), // Array of strings
  snowflake_tags: z.array(z.string()).optional().nullable(),
  reads: z.number().nullable(), // Nullable number
  writes: z.number(), // Number
  read_write_ratio: z.number().nullable(), // Nullable number
  owner: z.string(), // String
  instance_id: z.coerce.string(), // Assuming instance_id is a string
});

export const CostBreakdownSchema = z.object({
  breakdown: z.array(
    z.object({
      date: z.string(), // Assuming date is in string format (e.g., "2025-06-20")
      total_cost: z.number(), // Float values are represented as numbers in Zod
      insertion_cost: z.number(),
      storage_cost: z.number(),
      clustering_cost: z.number(),
    })
  ),
});
export type CostBreakdown = z.infer<typeof CostBreakdownSchema>;

export const AnnualizedCostSchema = z.object({
  annualized_total_cost: z.number(),
  annualized_insertion_cost: z.number(),
  annualized_storage_cost: z.number(),
  annualized_clustering_cost: z.number(),
});

export type AnnualizedCost = z.infer<typeof AnnualizedCostSchema>;

export const TableQueryResponseSchema = z.object({
  query_id: z.string(),
  hash: z.string(),
  query_text: z.string().optional(),
  insights: z.array(z.string()).optional(),
  instance_id: z.number().optional(),
  access_type: z.string(),
  user_name: z.string().optional(),
  warehouse_name: z.string().optional(),
  start_time: z.string().optional(),
  end_time: z.string().optional(),
  execution_time: z.number(),
  execution_status: z.string().optional(),
  query_cost: z.number(),
  bytes_scanned: z.number().optional(),
  rows_produced: z.number().optional(),
});

export const TableQueryFiltersResponseSchema = z.object({
  warehouses: z.array(z.string()),
  users: z.array(z.string()),
  access_types: z.array(z.string()),
  insights: z.array(z.string()),
});

export const TableQueryFiltersSchema = z.object({
  access_types: z.array(z.string()).optional(),
  user_names: z.array(z.string()).optional(),
  warehouse_names: z.array(z.string()).optional(),
  insights: z.array(z.string()).optional(),
  instance_ids: z.array(z.string()).optional(),
  execution_time_filter: z.string().optional(),
  execution_time_filter_mode: z.string().optional(),
  estimated_cost_filter: z.string().optional(),
  estimated_cost_filter_mode: z.string().optional(),
  page: z.string(),
  size: z.string(),
  start_date: z.string(),
  end_date: z.string(),
  order_by: z.string().optional(),
  sort_order: z.string().optional(),
});

export type TableQueryFilters = z.infer<typeof TableQueryFiltersSchema>;

export type TableQuery = z.infer<typeof TableQueryResponseSchema>;
export const TableQueriesResponseSchema = pageSchema(TableQueryResponseSchema);

export type TableQueriesResponse = z.infer<typeof TableQueriesResponseSchema>;
export type TableQueryFiltersResponse = z.infer<typeof TableQueryFiltersResponseSchema>;

export type Dataset = z.infer<typeof DatasetSchema>;
export const DatasetListResponseSchema = pageSchema(DatasetSchema);
export type DatasetListResponse = z.infer<typeof DatasetListResponseSchema>;

// Table Detail Response Schema
export const TableDetailResponseSchema = z.object({
  id: z.coerce.string(),
  datasource_type: z.string(),
  scope: z.array(z.string()),
  columns: z.array(z.any()).nullable().optional(),
  tags: z.array(BaseTagSchema).nullable().optional(),
  description: z.string().nullable().optional(),
  last_modified_by: z.number().nullable().optional(),
  instance_id: z.number().nullable().optional(),
});

export type TableDetailResponse = z.infer<typeof TableDetailResponseSchema>;

// Table Description Response Schema
export const TableDescriptionResponseSchema = z.object({
  description: z.string(),
});

export type TableDescriptionResponse = z.infer<typeof TableDescriptionResponseSchema>;

// Update Table Purpose Request/Response
export interface UpdateTablePurposeRequest {
  purpose: string;
}

// Table Insights Response Schema
export const TableInsightsResponseSchema = z.object({
  tags: z.array(
    z.object({
      label: z.string(),
      reason: z.string(),
    })
  ),
  custom_tag: z
    .object({
      reason: z.string(),
      user: z.number(),
    })
    .nullable()
    .optional(),
  insights: z.object({
    annualized_insert_cost: z.number().nullish(),
    estimated_annualized_storage_costs: z.number().nullish(),
    last_modified: z.string().nullish(),
    last_read: z.string().nullish(),
    owner: z.string().nullish(),
    read_write_ratio: z.string().nullish(),
    refresh_count: z.number().nullish(),
    annualized_refresh_cost: z.number().nullish(),
  }),
  // Display-ready labels from the backend, rendered as-is. Empty array is
  // expected when ingestion has not populated the table's type yet.
  table_types: z.array(z.string()).nullish(),
});

export type TableInsightsResponse = z.infer<typeof TableInsightsResponseSchema>;

export const DYNAMIC_TABLE_TYPE = "Dynamic Table";

// Custom Table Insight Request/Response
export interface PostCustomTableInsightRequest {
  id: string;
  data: Record<string, unknown>;
}

export const PostCustomTableInsightResponseSchema = z.object({
  ok: z.boolean(),
  error_code: z.string().optional(),
  message: z.string().optional(),
});

export type PostCustomTableInsightResponse = z.infer<typeof PostCustomTableInsightResponseSchema>;

// Get Table ID Request/Response
export interface GetTableIdRequest {
  table_rk: string;
}

export const GetTableIdResponseSchema = z.object({
  id: z.string(),
  table_id: z.coerce.string(),
});

export type GetTableIdResponse = z.infer<typeof GetTableIdResponseSchema>;

// Table Storage Over Time Response
export const TableStorageOverTimeResponseSchema = z.object({
  table_owner: z.string(),
  active_storage: z.string(),
  time_travel_storage: z.string(),
  failsafe_storage: z.string(),
  cloned_storage: z.string(),
  cluster_key: z.string().nullable().optional(),
  cluster_cost: z.string().nullable().optional(),
  is_dynamic_table: z.boolean().nullable().optional(),
  dynamic_table_attributes: z.record(z.any()).nullable().optional(),
});

export type TableStorageOverTimeResponse = z.infer<typeof TableStorageOverTimeResponseSchema>;

// Column Access History Request/Response
export interface GetColumnAccessHistoryParams {
  page: number;
  size: number;
  table_id?: string;
  column_rk?: string[];
  order_by?: string;
  sort_order?: string;
}

export const ColumnAccessHistoryItemSchema = z.object({
  column_count: z.number(),
  cost: z.number(),
  column: z.object({
    rk: z.string(),
    name: z.string(),
    path: z.string(),
  }),
  usage: z.number(),
  user_count: z.number(),
  queries: z.number(),
});

export const ColumnAccessHistoryResponseSchema = pageSchema(ColumnAccessHistoryItemSchema);

export type ColumnAccessHistoryItem = z.infer<typeof ColumnAccessHistoryItemSchema>;
export type ColumnAccessHistoryResponse = z.infer<typeof ColumnAccessHistoryResponseSchema>;

// Column User Access History Filters Response
export const ColumnUserAccessHistoryFiltersResponseSchema = z.array(
  z.object({
    rk: z.string(),
    name: z.string(),
    path: z.string(),
  })
);

export type ColumnUserAccessHistoryFiltersResponse = z.infer<
  typeof ColumnUserAccessHistoryFiltersResponseSchema
>;

// Column User Access History Request/Response
export interface GetColumnUserAccessHistoryRequest {
  column_rk: string;
}

export const ColumnUserAccessHistoryResponseSchema = z.object({
  num_queries: z.number(),
  user_access: z.array(
    z.object({
      user_name: z.string(),
      usage: z.number(),
    })
  ),
});

export type ColumnUserAccessHistoryResponse = z.infer<typeof ColumnUserAccessHistoryResponseSchema>;

// ===== Types moved from src/types/datasets.ts =====

// Column Schema
export const ColumnSchema = z.object({
  name: z.string(),
  datatype: z.string(),
  description: z.string().nullable().optional(),
  column_description: z.string().nullable().optional(),
  table_schema_id: z.number(),
  tags: z.array(BaseTagSchema).optional().default([]),
  last_modified_by: z.number().nullable().optional(),
  created_on: z.string().nullish(),
  description_frozen: z.boolean().nullish(),
});

export type Column = z.infer<typeof ColumnSchema>;

// Table Matches Schema
export const TableMatchesSchema = z.object({
  table_name: z.boolean().optional(),
  table_purpose_match: z.boolean().optional(),
  column_names: z.number().optional(),
  column_description_matches: z.number().optional(),
});

export type TableMatches = z.infer<typeof TableMatchesSchema>;

// Table Item Schema
export const TableItemSchema = z.object({
  id: z.coerce.string(),
  name: z.string(),
  datasource_type: z.string(),
  scope: z.array(z.string()),
  columns: z.array(ColumnSchema),
  purpose: z.string().nullable().optional(),
  // Display-ready Snowflake object type labels (AI-5218). Nullish so an older
  // backend that does not send the field yet does not fail validation.
  table_types: z.array(z.string()).nullish(),
  tags: z.array(BaseTagSchema).optional().default([]),
  matches: TableMatchesSchema.optional(),
  last_modified_by: z.number().nullable().optional(),
  purpose_frozen: z.boolean().nullish(),
  // Snowflake object tags ("name:value" tokens); nullish so a backend that
  // does not send the field yet does not fail validation.
  snowflake_tags: z.array(z.string()).optional().nullable(),
});

export type TableItem = z.infer<typeof TableItemSchema>;

// Tables Response Schema (paginated)
export const TablesResponseSchema = z.object({
  items: z.array(TableItemSchema),
  total: z.number(),
  page: z.number(),
  pages: z.number(),
  size: z.number(),
});

export type TablesResponse = z.infer<typeof TablesResponseSchema>;

// Table Filters Response Schema
export const TableFiltersResponseSchema = z.record(
  z.string(), // datasource_type
  z.record(
    z.string(), // instance_id
    z.record(
      z.string(), // database
      z.record(z.string(), z.number()) // schema -> count
    )
  )
);

export type TableFiltersResponse = z.infer<typeof TableFiltersResponseSchema>;

// Tags Response Schema
export const TagsResponseSchema = z.object({
  column_badges: z.array(z.string()).optional().default([]),
});

export type TagsResponse = z.infer<typeof TagsResponseSchema>;

// Recent Tables Response Schema
export const RecentTableItemSchema = z.object({
  resource_id: z.coerce.string(),
  resource_path: z.string(),
  resource_name: z.string(),
  datasource_type: z.string(),
  access_time: z.number(),
});

export const RecentTablesResponseSchema = z.array(RecentTableItemSchema);

export type RecentTableItem = z.infer<typeof RecentTableItemSchema>;
export type RecentTablesResponse = z.infer<typeof RecentTablesResponseSchema>;

// Table RK Response Schema
export const TableRkResponseSchema = z.object({
  table_rk: z.string(),
});

export type TableRkResponse = z.infer<typeof TableRkResponseSchema>;

// Update Column Description Request Schema
export const UpdateColumnDescriptionRequestSchema = z.object({
  description: z.string().nullable(),
});

export type UpdateColumnDescriptionRequest = z.infer<typeof UpdateColumnDescriptionRequestSchema>;

// Get Tables Request Parameters
export interface GetTablesParams {
  page?: number;
  size?: number;
  keyword?: string;
  datasource_type?: string;
  datasources?: string[];
  databases?: string[];
  schemas?: string[];
  tags?: string[];
  snowflakeTags?: string[];
  table_types?: string[];
}
